import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Coffee, UtensilsCrossed, Sparkles, Award, MapPin, Clock } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";
import { SectionHeading } from "@/components/site/SectionHeading";

const InaugurationHighlight = ({ inaug }) => (
  <div className="mb-8 overflow-hidden rounded-2xl border border-gold/30 bg-gradient-navy p-6 shadow-elevated sm:p-8">
    <div className="flex items-center gap-2 text-gold-soft">
      <Sparkles className="h-4 w-4" />
      <span className="text-xs font-semibold uppercase tracking-[0.22em]">{inaug.label}</span>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-primary-foreground/80">
      <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold-soft" /> {inaug.date}</span>
      <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold-soft" /> {inaug.venue}</span>
    </div>
    <div className="mt-6 grid gap-6 sm:grid-cols-2">
      <div className="border-l-2 border-gold/50 pl-4">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold-soft">{inaug.speechLabel}</p>
        <p className="font-display mt-1.5 text-lg text-primary-foreground">{inaug.speechName}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-primary-foreground/65">{inaug.speechRole}</p>
      </div>
      <div className="border-l-2 border-gold/50 pl-4">
        <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold-soft">{inaug.welcomeLabel}</p>
        <p className="font-display mt-1.5 text-lg text-primary-foreground">{inaug.welcomeName}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-primary-foreground/65">{inaug.welcomeRole}</p>
      </div>
    </div>
  </div>
);

const TimelineRow = ({ item, isLast, lunchLabel }) => {
  const isBreak = Boolean(item.break);
  const isCeremony = item.inauguration || item.closing;
  return (
    <div className="relative grid grid-cols-[auto_1fr] gap-x-5 sm:grid-cols-[9.5rem_auto_1fr]">
      <div className="hidden pt-0.5 text-right sm:block">
        <span className="text-sm font-medium tabular-nums text-primary">{item.time}</span>
      </div>
      <div className="relative flex flex-col items-center">
        <span className={`z-10 mt-1 h-3 w-3 rounded-full ring-4 ring-background ${isBreak ? "bg-muted-foreground/40" : isCeremony ? "bg-gold" : "bg-teal"}`} />
        {!isLast && <span className="w-px flex-1 bg-border" aria-hidden="true" />}
      </div>
      <div className={`pb-8 ${isLast ? "pb-0" : ""}`}>
        <span className="mb-1 block text-xs font-medium tabular-nums text-muted-foreground sm:hidden">{item.time}</span>
        {isBreak ? (
          <div className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/60 px-4 py-2.5 text-sm font-medium text-muted-foreground">
            {item.break === lunchLabel ? <UtensilsCrossed className="h-4 w-4" /> : <Coffee className="h-4 w-4" />}
            {item.break}
          </div>
        ) : isCeremony ? (
          <div className="rounded-xl border border-gold/40 bg-gold/[0.07] px-5 py-4">
            <div className="flex items-center gap-2 text-gold-ink">
              {item.closing ? <Award className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              <span className="font-display text-base font-medium">{item.topic}</span>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card px-5 py-4 shadow-card transition-shadow duration-300 hover:shadow-elevated">
            <p className="font-display text-base font-medium text-primary">{item.speaker}</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.topic}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const Schedule = () => {
  const { t } = useI18n();
  const s = t.schedule;
  const [active, setActive] = useState("d1");
  const reduce = useReducedMotion();
  const days = [
    { key: "d1", tab: s.day1Tab, items: s.day1 },
    { key: "d2", tab: s.day2Tab, items: s.day2 },
  ];
  const current = days.find((d) => d.key === active) || days[0];

  return (
    <section id="schedule" className="scroll-mt-24 bg-gradient-ivory">
      <div className="container-site section-pad py-20 md:py-24">
        <SectionHeading eyebrow={s.eyebrow} title={s.title} description={s.desc} />

        <div className="mt-10 inline-flex rounded-xl border border-border bg-card p-1 shadow-card">
          {days.map((day) => (
            <button
              key={day.key}
              onClick={() => setActive(day.key)}
              className={`relative rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                active === day.key ? "text-primary-foreground" : "text-muted-foreground hover:text-primary"
              }`}
            >
              {active === day.key && (
                <motion.span layoutId="scheduleTab" className="absolute inset-0 rounded-lg bg-primary" transition={{ type: "spring", stiffness: 380, damping: 32 }} />
              )}
              <span className="relative">{day.tab}</span>
            </button>
          ))}
        </div>

        <div className="mt-10 max-w-3xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {active === "d2" && <InaugurationHighlight inaug={s.inaug} />}
              <div>
                {current.items.map((item, i) => (
                  <TimelineRow key={i} item={item} isLast={i === current.items.length - 1} lunchLabel={s.lunchBreak} />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
